/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/useQaItpExportCover.ts
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-COVER-HOOK-001
 * PURPOSE: Cover photo for the open ITP export: a pick uploads and lands in the preview.
 * LANDMINE: The upload resolves after later renders. Read dirty, signed and html from the ref, or an edit made during the upload drops the photo.
 */
import { useRef, useState } from 'react'
import { patchItpCoverImage, patchItpInclude } from '../../../qa/patchItpCover'
import type { ItpCoverFields } from '../../../qa/itpCover'
import type { useInspectionRecordSettings } from '../useInspectionRecordSettings'

type Details = ReturnType<typeof useInspectionRecordSettings>

export function useQaItpExportCover({
  details,
  dirty,
  signed,
  editedHtml,
  setEditedHtml,
  setLivePatch,
  reportError,
}: {
  details: Details
  dirty: boolean
  signed: boolean
  editedHtml: string
  setEditedHtml: (html: string) => void
  setLivePatch: (html: string | null) => void
  reportError: (err: unknown) => void
}) {
  const live = useRef({ dirty, signed, editedHtml })
  live.current = { dirty, signed, editedHtml }
  const [coverBusy, setCoverBusy] = useState(false)
  // Reused from: packages/job-hub/src/jobs/qa/patchItpCover.ts (patchItpCoverImage)
  const patchCoverInPlace = (
    url: string | null,
    size = { widthMm: details.coverWidthMm, heightMm: details.coverHeightMm },
  ) => {
    const now = live.current
    if (!(now.dirty || now.signed)) return
    const next = patchItpCoverImage(now.editedHtml, url, size)
    setEditedHtml(next)
    setLivePatch(next)
  }
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/JobQaQuoteDetails.tsx (Cover image input: details.uploadCover / details.clearCover)
  const chooseCover = async (file: File | null) => {
    setCoverBusy(true)
    try {
      patchCoverInPlace(file ? await details.uploadCover(file) : await details.clearCover())
    } catch (err) {
      reportError(err)
    } finally {
      setCoverBusy(false)
    }
  }
  const setCoverSize = (widthMm: number, heightMm: number) => {
    details.setCoverWidthMm(widthMm)
    details.setCoverHeightMm(heightMm)
    if (details.coverImageUrl) patchCoverInPlace(details.coverImageUrl, { widthMm, heightMm })
  }
  const patchIncludeInPlace = (include: ItpCoverFields['include']) => {
    const now = live.current
    if (!(now.dirty || now.signed)) return
    const next = patchItpInclude(now.editedHtml, include)
    setEditedHtml(next)
    setLivePatch(next)
  }
  return {
    coverBusy,
    chooseCover,
    patchCoverInPlace,
    patchIncludeInPlace,
    setCoverWidthMm: (value: number) => setCoverSize(value, details.coverHeightMm),
    setCoverHeightMm: (value: number) => setCoverSize(details.coverWidthMm, value),
  }
}
